export type PlayerType =
  | 'OBSERVADOR_ATRAPADO'
  | 'BUSCADOR_BUCLE'
  | 'PROTECTOR_CONTROL'
  | 'REBELDE_CANSADO'

export type RootCode =
  | 'MENTE'
  | 'TIEMPO'
  | 'IDENTIDAD'
  | 'MIEDO'
  | 'DISTRACCION'

export interface PlayerTypeData {
  id: PlayerType
  title: string
  subtitle: string
  validation: string
  blindSpot: string
  favoriteProgram: string
  signal: string
  iconName: 'Eye' | 'Compass' | 'Shield' | 'Flame'
}

export interface RootCodeData {
  id: RootCode
  name: string
  description: string
}

export const playerTypes: Record<PlayerType, PlayerTypeData> = {
  OBSERVADOR_ATRAPADO: {
    id: 'OBSERVADOR_ATRAPADO',
    title: 'El Observador Atrapado',
    subtitle: 'Alta consciencia, baja salida',
    validation:
      'Tú "ves" mucho. Notas señales, patrones, energía, incoherencias… pero cuando toca mover ficha, tu mente abre 20 pestañas internas.',
    blindSpot: 'Confundes entender con liberarte.',
    favoriteProgram: 'Analizar para sentir control.',
    signal:
      'Terminas el día con sensación de "sé demasiado… pero no me siento mejor".',
    iconName: 'Eye',
  },
  BUSCADOR_BUCLE: {
    id: 'BUSCADOR_BUCLE',
    title: 'El Buscador en Bucle',
    subtitle: 'Mucho método, poca integración',
    validation:
      'Tú no eres flojo/a. Eres curioso/a, hambriento/a de verdad. El problema es que tu sistema está entrenado para buscar "la pieza que falta" y saltar al siguiente enfoque.',
    blindSpot: 'No necesitas otro método. Necesitas una secuencia.',
    favoriteProgram: 'Más información = más seguridad.',
    signal: 'Te emocionas, empiezas… y a los días vuelves al mismo loop.',
    iconName: 'Compass',
  },
  PROTECTOR_CONTROL: {
    id: 'PROTECTOR_CONTROL',
    title: 'El Protector del Control',
    subtitle: 'Seguridad primero, libertad después',
    validation:
      'Tu sistema nervioso aprendió que "si no controlo, algo pasa". Por fuera funcionas. Por dentro, estás siempre anticipando.',
    blindSpot: 'Tu control no es tu personalidad… es una armadura.',
    favoriteProgram: 'Prevenir el dolor antes de vivir.',
    signal: 'Cuando todo va bien, igual cuesta relajarte.',
    iconName: 'Shield',
  },
  REBELDE_CANSADO: {
    id: 'REBELDE_CANSADO',
    title: 'El Rebelde Cansado',
    subtitle: 'Ves el juego… pero estás drenado/a',
    validation:
      'Tú ya oliste la Matrix. Ya viste manipulación, superficialidad, teatro. Pero tanta lucidez sin dirección puede convertirse en cansancio o cinismo.',
    blindSpot: 'Confundes "ver el juego" con "estar por encima del juego".',
    favoriteProgram: 'Desconectarte para no decepcionarte.',
    signal: 'Tu intuición está viva, pero tu energía no acompaña.',
    iconName: 'Flame',
  },
}

export const rootCodes: Record<RootCode, RootCodeData> = {
  MENTE: {
    id: 'MENTE',
    name: 'Mente',
    description: 'Tu ruido mental está actuando como la jaula.',
  },
  TIEMPO: {
    id: 'TIEMPO',
    name: 'Tiempo',
    description: 'La prisa/urgencia es el hechizo que te mantiene reactivo/a.',
  },
  IDENTIDAD: {
    id: 'IDENTIDAD',
    name: 'Identidad',
    description:
      'Tus roles y expectativas están escribiendo tu historia sin permiso.',
  },
  MIEDO: {
    id: 'MIEDO',
    name: 'Miedo',
    description: 'El miedo está decidiendo por ti antes de que tú elijas.',
  },
  DISTRACCION: {
    id: 'DISTRACCION',
    name: 'Distracción',
    description:
      'Tu atención está siendo secuestrada: ese es el combustible del sistema.',
  },
}

export const goalLabels: Record<string, string> = {
  GOAL_PEACE: 'paz interna',
  GOAL_DECISIONS: 'decisiones claras',
  GOAL_ENERGY: 'energía real',
  GOAL_RELATIONSHIPS: 'relaciones sanas',
  GOAL_DIRECTION: 'dirección clara',
}

export const painLabels: Record<string, string> = {
  PAIN_ANXIETY: 'ansiedad',
  PAIN_EMPTY: 'vacío',
  PAIN_CONFUSION: 'confusión',
  PAIN_EXHAUSTED: 'cansancio',
  PAIN_FEAR: 'miedo',
}

export const commitLabels: Record<string, string> = {
  COMMIT_5: '3–5 minutos',
  COMMIT_10: '10 minutos',
  COMMIT_20: '20 minutos',
  COMMIT_30: '30 minutos',
  COMMIT_SIMPLE: 'ultra simple',
}

export const testimonials = [
  {
    quote: 'La recomiendo muchísimo, la diferencia se nota al instante.',
    author: 'Sofia M.',
    type: 'OBSERVADOR_ATRAPADO' as PlayerType,
    image:
      'https://cdn.magicpatterns.com/uploads/bzZNUt4ysZvto3RqVkGL55/t1.png',
  },
  {
    quote: 'Volví a sentir que la vida estaba viva.',
    author: 'Andrés R.',
    type: 'BUSCADOR_BUCLE' as PlayerType,
    image:
      'https://cdn.magicpatterns.com/uploads/9N5dLjTzD8PU7TVwbyczuc/t3.png',
  },
  {
    quote: 'Mi ansiedad bajó cuando entendí el "programa".',
    author: 'Valeria S.',
    type: 'REBELDE_CANSADO' as PlayerType,
    image:
      'https://cdn.magicpatterns.com/uploads/2EUErGgNsmqjJ7fLnvypdM/t6.png',
  },
]

export const faqItems = [
  {
    question: '¿Esto es para mí si no soy "espiritual"?',
    answer:
      'Sí. Solo necesitas curiosidad y ganas de salir del loop. No es religión.',
  },
  {
    question: '¿Cuánto tarda en sentirse algo?',
    answer:
      'Muchos sienten un cambio dentro de los primeros capítulos, porque cambia el marco desde el que miras tu vida.',
  },
  {
    question: '¿Y si me da miedo "despertar"?',
    answer:
      'Tu plan está diseñado para despertar sin perderte: con estructura, integración y pasos simples.',
  },
  {
    question: '¿Cómo recibo el contenido?',
    answer:
      'Acceso digital inmediato (eBook + audio) para usar en cualquier dispositivo.',
  },
]
